import { useEffect, useState } from "react";

interface Food {
  id: string;
  name: string;
  en: string;
  tag: string;
  desc: string;
  detail: string;
  color: string;
  textColor: string;
  image: string;
}

const foods: Food[] = [
  {
    id: "satang",
    name: "沙茶面",
    en: "Sa Cha Noodles",
    tag: "必吃 No.1",
    desc: "鲜香浓郁，闽南灵魂",
    detail: "沙茶面是厦门最具代表性的小吃，以沙茶酱为底料，配以猪血、豆腐、大肠等食材，汤底浓郁鲜香，入口回甘，是每个厦门人心中无可替代的家乡味道。",
    color: "bg-amber-50",
    textColor: "text-amber-700",
    image: "https://public.readdy.ai/ai/img_res/db26f686967a5875ce8b2d5ffd6f7ea9.jpg",
  },
  {
    id: "oyster",
    name: "海蛎煎",
    en: "Oyster Omelette",
    tag: "闽南经典",
    desc: "鲜嫩酥脆，烟火滋味",
    detail: "海蛎煎是闽南地区最受欢迎的传统小吃之一，以新鲜海蛎、鸡蛋、地瓜粉为主料，煎至外酥内嫩，配以特制酱料，鲜香四溢，是夜市摊头最动人的烟火气。",
    color: "bg-orange-50",
    textColor: "text-orange-700",
    image: "https://readdy.ai/api/search-image?query=Oyster%20omelette%20Taiwanese%20Minnan%20style%2C%20golden%20crispy%20egg%20pancake%20with%20fresh%20oysters%2C%20sweet%20potato%20starch%2C%20garnished%20with%20cilantro%20and%20sweet%20chili%20sauce%2C%20sizzling%20on%20iron%20pan%2C%20street%20food%20photography%2C%20warm%20golden%20light%2C%20appetizing%20close%20up%2C%20simple%20background&width=640&height=800&seq=food002&orientation=portrait",
  },
  {
    id: "peanut",
    name: "花生汤",
    en: "Peanut Soup",
    tag: "甜蜜暖心",
    desc: "绵软香甜，温润如玉",
    detail: "厦门花生汤以精选花生慢火熬煮，汤色乳白，花生软糯不烂，甜而不腻。配上油条或炸枣，是厦门人早餐桌上最温暖的一碗，承载着几代人的记忆与情感。",
    color: "bg-yellow-50",
    textColor: "text-yellow-700",
    image: "https://readdy.ai/api/search-image?query=Chinese%20peanut%20soup%20dessert%20in%20white%20ceramic%20bowl%2C%20creamy%20white%20broth%20with%20soft%20cooked%20peanuts%2C%20traditional%20Fujian%20style%2C%20warm%20cozy%20lighting%2C%20steam%20rising%20gently%2C%20simple%20elegant%20presentation%2C%20food%20photography%2C%20clean%20background%2C%20ultra%20detailed&width=640&height=800&seq=food003&orientation=portrait",
  },
  {
    id: "bawan",
    name: "肉粽",
    en: "Minnan Rice Dumpling",
    tag: "节庆珍味",
    desc: "层层包裹，岁月芬芳",
    detail: "闽南肉粽与北方粽子截然不同，以糯米、五花肉、香菇、虾米、栗子等精心搭配，蒸制后油润饱满，咸香入味。每一口都是闽南人对传统节庆的深情记忆。",
    color: "bg-green-50",
    textColor: "text-green-700",
    image: "https://readdy.ai/api/search-image?query=Minnan%20Fujian%20style%20zongzi%20rice%20dumpling%20unwrapped%20on%20plate%2C%20glutinous%20rice%20with%20pork%20belly%20mushroom%20shrimp%20chestnut%20filling%2C%20bamboo%20leaf%20wrapping%2C%20traditional%20Chinese%20food%20photography%2C%20warm%20natural%20lighting%2C%20rustic%20wooden%20background%2C%20ultra%20detailed%20appetizing&width=640&height=800&seq=food004&orientation=portrait",
  },
];

interface FoodPageProps {
  visible: boolean;
  onNext: () => void;
  onPrev: () => void;
}

export default function FoodPage({ visible, onNext, onPrev }: FoodPageProps) {
  const [entered, setEntered] = useState(false);
  const [activeFood, setActiveFood] = useState<Food | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntered(true), 80);
      return () => clearTimeout(t);
    } else {
      setEntered(false);
      setActiveFood(null);
    }
  }, [visible]);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-stone-100 flex flex-col">
      {/* Top Nav */}
      <nav className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <img
          src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png"
          alt="Logo"
          className="h-8 md:h-9 w-auto object-contain"
        />
        <div className="hidden md:flex items-center gap-6">
          {["山海", "人文", "美食", "活力"].map((item, i) => (
            <span
              key={item}
              className={`text-xs tracking-widest cursor-pointer transition-colors whitespace-nowrap ${i === 2 ? "text-red-600 font-semibold" : "text-gray-500 hover:text-gray-800"}`}
            >
              {item}
            </span>
          ))}
        </div>
      </nav>

      {/* Mobile: vertical stack; Desktop: horizontal */}
      <div className="flex flex-col md:flex-row h-full pt-14 md:pt-0">

        {/* Title Area — horizontal strip on mobile, left column on desktop */}
        <div
          className={`relative flex flex-row md:flex-col items-center md:justify-center px-5 md:pl-12 md:pr-8 py-3 md:py-0 w-full md:w-72 lg:w-80 flex-shrink-0 transition-all duration-800 ${entered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
        >
          {/* Decorative bg text — hidden on mobile */}
          <div
            className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 text-[120px] font-bold text-stone-200 leading-none select-none pointer-events-none"
            style={{ fontFamily: "'Noto Serif SC', serif", writingMode: "vertical-rl" }}
          >
            闽南
          </div>

          <div className="relative z-10 flex flex-row md:flex-col items-center md:items-start gap-3 md:gap-0">
            <div className="flex items-center gap-2 md:mb-5">
              <span className="text-red-500 text-xs tracking-[0.2em] font-medium">04 / FOOD</span>
              <div className="w-4 md:w-6 h-px bg-red-300" />
            </div>
            <h2
              className="text-2xl md:text-6xl font-light text-gray-900 leading-tight md:mb-3"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              舌尖<span className="text-red-600 md:block">闽南</span>
            </h2>
            <div className="hidden md:block w-8 h-0.5 bg-red-400 mb-4" />
            <p className="hidden md:block text-gray-500 text-sm leading-relaxed">
              鲜香地道，烟火人间
              <br />
              风味长存，念念不忘
            </p>
            <div className="hidden md:flex items-center gap-2 mt-6 text-gray-400 text-xs">
              <i className="ri-cursor-line" />
              <span>点击美食查看详情</span>
            </div>
          </div>
        </div>

        {/* Food Cards Grid */}
        <div className="flex-1 flex items-center justify-center px-3 md:px-6 pb-10 md:pb-16 overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 w-full max-w-4xl">
            {foods.map((food, i) => (
              <div
                key={food.id}
                onClick={() => setActiveFood(activeFood?.id === food.id ? null : food)}
                onMouseEnter={() => setHoveredId(food.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`relative rounded-xl md:rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                  entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                } ${hoveredId === food.id ? "-translate-y-1 md:-translate-y-2" : ""} ${activeFood?.id === food.id ? "ring-2 ring-red-400" : ""}`}
                style={{ transitionDelay: `${i * 80}ms`, height: "clamp(160px, 28vw, 320px)" }}
              >
                <img
                  src={food.image}
                  alt={food.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500"
                  style={{ transform: hoveredId === food.id ? "scale(1.05)" : "scale(1)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                <div className="absolute top-2 left-2 md:top-3 md:left-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${food.color} ${food.textColor}`}>
                    {food.tag}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
                  <p className="text-white/60 text-xs mb-0.5 hidden md:block">{food.en}</p>
                  <h3
                    className="text-white text-base md:text-xl font-semibold mb-0.5"
                    style={{ fontFamily: "'Noto Serif SC', serif" }}
                  >
                    {food.name}
                  </h3>
                  <p className="text-white/70 text-xs hidden sm:block">{food.desc}</p>
                </div>

                <div
                  className={`absolute top-2 right-2 md:top-3 md:right-3 w-6 h-6 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all duration-300 ${hoveredId === food.id ? "opacity-100" : "opacity-0"}`}
                >
                  <i className="ri-add-line text-white text-sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Food Detail Modal */}
      {activeFood && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setActiveFood(null)}
        >
          <div
            className="bg-white rounded-2xl md:rounded-3xl overflow-hidden w-full max-w-lg flex flex-col sm:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-40 sm:w-40 sm:h-auto flex-shrink-0">
              <img src={activeFood.image} alt={activeFood.name} className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 p-4 md:p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-2 md:mb-3">
                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${activeFood.color} ${activeFood.textColor} font-medium`}>
                      {activeFood.tag}
                    </span>
                    <h3
                      className="text-xl md:text-2xl font-semibold text-gray-900 mt-1.5"
                      style={{ fontFamily: "'Noto Serif SC', serif" }}
                    >
                      {activeFood.name}
                    </h3>
                    <p className="text-gray-400 text-xs mt-0.5">{activeFood.en}</p>
                  </div>
                  <button
                    onClick={() => setActiveFood(null)}
                    className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-gray-500 text-sm" />
                  </button>
                </div>
                <div className="w-8 h-0.5 bg-red-400 mb-2 md:mb-3" />
                <p className="text-gray-600 text-xs md:text-sm leading-relaxed">{activeFood.detail}</p>
              </div>
              <div className="flex items-center gap-2 mt-3 md:mt-4 text-red-500 text-xs">
                <i className="ri-map-pin-line" />
                <span>厦门各大街巷均可寻觅</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Arrows */}
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/80 border border-gray-200 text-gray-600 hover:bg-white transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={onNext}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Page indicator */}
      <div className="absolute bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`rounded-full transition-all duration-300 ${i === 3 ? "w-6 h-1.5 bg-red-500" : "w-1.5 h-1.5 bg-gray-300"}`}
          />
        ))}
      </div>
    </div>
  );
}
